import { ApiClient } from './client';
import { makeStores, type SessionState, type StorageArea } from '../storage/storage';

// Account session state (§12.4/§14.2). Holds the authToken (persisted in the `kolony.session` store) and the
// current account; the ApiClient reads the token live. On a 401 the session self-clears (the token is stale).
// The resumeRoom half of the session blob is preserved across sign-in/out.

export interface Account {
  id: string;
  email: string;
  displayName: string;
  createdAt: number;
  /**
   * D248: present (and only ever `true`) when this account may open the operator's
   * dashboard. It is a DRAWING hint, never a permission — the server re-derives the answer
   * from the bearer token on every admin request.
   */
  admin?: boolean;
}

export class AuthSession {
  private token: string | null;
  account: Account | null = null;
  readonly api: ApiClient;
  private readonly stores: ReturnType<typeof makeStores>;

  constructor(area: StorageArea = localStorage) {
    this.stores = makeStores(area);
    this.token = this.stores.session.load().authToken ?? null;
    this.api = new ApiClient(() => this.token);
  }

  get signedIn(): boolean {
    return this.token !== null;
  }

  /** The current authToken (for passing to the WS lobby), or null. */
  getToken(): string | null {
    return this.token;
  }

  setToken(token: string | null): void {
    this.token = token;
    const cur = this.stores.session.load();
    const next: SessionState = { v: 1 };
    if (token) next.authToken = token;
    if (cur.resumeRoom) next.resumeRoom = cur.resumeRoom;
    this.stores.session.save(next);
  }

  /** GET /me to (re)load the account; a 401 (stale token) clears the session. */
  async refresh(): Promise<Account | null> {
    if (!this.token) {
      this.account = null;
      return null;
    }
    try {
      const r = await this.api.get<{ account: Account }>('/me');
      this.account = r.account;
      return r.account;
    } catch {
      this.setToken(null);
      this.account = null;
      return null;
    }
  }

  /** Exchange a magic-link token for a session (verify), persisting the authToken on success. */
  async verify(magicToken: string): Promise<Account> {
    const r = await this.api.post<{ authToken: string; account: Account }>('/auth/verify', { token: magicToken });
    this.setToken(r.authToken);
    this.account = r.account;
    return r.account;
  }

  async signOut(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch {
      /* best-effort — clear locally regardless */
    }
    this.setToken(null);
    this.account = null;
  }
}
